/**
 * Normalizes gateway/client error strings into actionable chat UX copy.
 *
 * UX-PLAN Pillar G (Gap 9) — when the gateway blocks a chat.send with a
 * guardrail finding, the structured details live on `ErrorShape.details`
 * (see `src/gateway/server-methods/chat.ts` → input guardrail block). We
 * parse those details into a typed `ChatGuardrailFinding` so the chat view
 * can render `<chat-guardrail-banner>` with rule + limit + current size
 * and a Truncate-and-retry affordance for length-based rules.
 */

import type { GatewayErrorShape } from "../gateway-errors.ts";

/**
 * Typed guardrail finding parsed from `ErrorShape.details.guardrail` or
 * from the legacy fallback message ("blocked by chat guardrail: <rule>").
 *
 * `currentSize`/`limit`/`attachmentIndex` are only present for length
 * rules (`max_length`, `min_length`, `attachment_max_length`); callers
 * should treat them as optional and fall back to the banner's
 * non-numeric variant when undefined.
 */
export type ChatGuardrailFinding = {
  rule: string;
  /**
   * JAISIU-types-sweep: rule discriminator driving the banner UI
   * copy. Rules are not exhaustive (new rule names can land in the
   * backend without a UI change), so we model `kind` as the named
   * set the banner currently renders plus a string fallback for
   * anything newer. `kind` is optional because guardrail findings
   * produced by older gateway versions omit it; the banner falls
   * back to `rule` for display.
   */
  kind?:
    | "max_length"
    | "min_length"
    | "attachment_max_length"
    | "prompt_injection"
    | "pii"
    | "attachment"
    | "rate_limit"
    | "system"
    | (string & {});
  action: "block" | "redact" | "warn" | "allow";
  message: string;
  detail?: string;
  /**
   * JAISIU-types-sweep: prompt-injection detector tags the rule it
   * matched against (e.g. `"role_injection"`, `"system_prompt_leak"`).
   * Used by the banner copy in chat-guardrail-banner.ts to say which
   * specific pattern tripped the rule. Optional because only
   * `prompt_injection` rules carry a non-undefined value.
   */
  matchedPattern?: string | null;
  /** Optional numeric context — parsed from the rule's message suffix. */
  currentSize?: number;
  limit?: number;
  /** Which attachment tripped the rule (for `attachment_max_length`). */
  attachmentIndex?: number;
};

export type ChatErrorDisplay = {
  title: string;
  detail: string;
  recover?: string;
  /** UX-PLAN Pillar G: present when the server-side guardrail blocked
   *  the chat.send. Drives `<chat-guardrail-banner>` rendering. */
  guardrail?: ChatGuardrailFinding;
  /** UX-PLAN Pillar G: present when an output-side redaction was
   *  applied to the assistant reply (PII / forbidden pattern / length
   *  cap). Drives the "Partially redacted" footnote chip. */
  redacted?: { categories: ReadonlyArray<string>; truncated: boolean };
};

export type ChatErrorInput = {
  message: string;
  code?: string;
  retryable?: boolean;
  retryAfterMs?: number;
  /** UX-PLAN Pillar G: server-side details blob from `ErrorShape.details`.
   *  When present and shaped like a guardrail finding, takes precedence
   *  over the message-string fallback. */
  details?: unknown;
};

// ---------- Input shape coercion helpers ----------

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function readFiniteNumber(value: unknown): number | undefined {
  return isFiniteNumber(value) ? value : undefined;
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Pull a guardrail finding out of an `ErrorShape.details` blob.
 *
 * The gateway emits details as `{ guardrail: { rule, action, detail, message, ...} }`
 * or sometimes directly as the finding shape. We accept both.
 */
export function readGuardrailFindingFromDetails(details: unknown): ChatGuardrailFinding | null {
  if (!details || typeof details !== "object") {
    return null;
  }
  const obj = details as Record<string, unknown>;
  const candidate =
    obj.guardrail && typeof obj.guardrail === "object"
      ? (obj.guardrail as Record<string, unknown>)
      : (obj as Record<string, unknown>);
  const rule = readOptionalString(candidate.rule);
  if (!rule) {
    return null;
  }
  const actionRaw = readOptionalString(candidate.action);
  const action: ChatGuardrailFinding["action"] =
    actionRaw === "block" || actionRaw === "redact" || actionRaw === "warn" || actionRaw === "allow"
      ? actionRaw
      : "block";
  const message = readOptionalString(candidate.message) ?? "";
  const detail = readOptionalString(candidate.detail);
  const finding: ChatGuardrailFinding = { rule, action, message };
  if (detail !== undefined) finding.detail = detail;
  const currentSize = readFiniteNumber(candidate.currentSize);
  const limit = readFiniteNumber(candidate.limit);
  if (currentSize !== undefined) finding.currentSize = currentSize;
  if (limit !== undefined) finding.limit = limit;
  const attachmentIndex = readFiniteNumber(candidate.attachmentIndex);
  if (attachmentIndex !== undefined) finding.attachmentIndex = attachmentIndex;
  return finding;
}

/**
 * Legacy fallback: when `details` is not available but the error message
 * starts with the gateway's guardrail block string, parse out the rule
 * and length suffix.
 *
 * Server message format: `"blocked by chat guardrail: <rule>"` and the
 * underlying finding.message contains things like
 * `"message exceeds max length (N > M)"` — we recover `currentSize` and
 * `limit` from those parens when possible.
 */
export function readGuardrailFindingFromMessage(text: string): ChatGuardrailFinding | null {
  const m = /blocked\s+by\s+chat\s+guardrail:\s*([a-z_]+)/i.exec(text);
  if (!m) {
    return null;
  }
  const rule = (m[1] ?? "").toLowerCase();
  // Try to extract numeric context. Different rules use different formats:
  //   max_length:           "message exceeds max length (N > M)"
  //   min_length:           "message too short (N < M)"
  //   attachment_max_length:"attachment[i] exceeds max length (N > M)"
  let currentSize: number | undefined;
  let limit: number | undefined;
  let attachmentIndex: number | undefined;
  const lengthMatch = /\((\d+)\s*(<|>)\s*(\d+)\)/.exec(text);
  if (lengthMatch) {
    const left = Number(lengthMatch[1]);
    const op = lengthMatch[2];
    const right = Number(lengthMatch[3]);
    if (op === ">") {
      currentSize = left;
      limit = right;
    } else if (op === "<") {
      currentSize = left;
      limit = right;
    }
  }
  const attachmentMatch = /attachment\[(\d+)\]/i.exec(text);
  if (attachmentMatch) {
    attachmentIndex = Number(attachmentMatch[1]);
  }
  const finding: ChatGuardrailFinding = {
    rule,
    action: "block",
    message: text,
  };
  if (currentSize !== undefined) finding.currentSize = currentSize;
  if (limit !== undefined) finding.limit = limit;
  if (attachmentIndex !== undefined) finding.attachmentIndex = attachmentIndex;
  return finding;
}

/**
 * Convert output-guardrail warnings (from `redactReplyParts`) into the
 * display shape that powers the "Partially redacted" footnote.
 *
 * Buckets:
 *   - PII redactions → `"pii"`
 *   - Forbidden-pattern redactions → `"forbidden"`
 *   - Length truncations → `"truncated"` (also sets `redacted.truncated = true`)
 *
 * Returns `null` when nothing happened, so the footnote is omitted.
 */
export function redactedSummaryFromGuardrailWarnings(
  warnings: ReadonlyArray<{ rule: string; action: string }> | undefined | null,
): { categories: ReadonlyArray<string>; truncated: boolean } | null {
  if (!warnings || warnings.length === 0) {
    return null;
  }
  const categories = new Set<string>();
  let truncated = false;
  for (const w of warnings) {
    if (w.action !== "redact") continue;
    if (w.rule === "pii") {
      categories.add("pii");
    } else if (w.rule === "forbidden_pattern") {
      categories.add("forbidden");
    } else if (w.rule === "max_length") {
      categories.add("truncated");
      truncated = true;
    } else {
      categories.add(w.rule);
    }
  }
  if (categories.size === 0) {
    return null;
  }
  return { categories: Array.from(categories), truncated };
}

function buildAllModelsFailedRecover(text: string): string {
  const parts = text
    .replace(/^All models failed \(\d+\):\s*/i, "")
    .split(" | ")
    .map((p) => p.trim())
    .filter(Boolean);
  const hints: string[] = [];
  const billingExhausted = parts.some(
    (p) =>
      /\(billing\)/i.test(p) ||
      /\b402\b/.test(p) ||
      /insufficient[_ ]quota|credit balance|out of credits|monthly_token_quota/i.test(p),
  );
  if (billingExhausted) {
    hints.push(
      "Credits or quota exhausted — top up at https://pryzm.at (Account → Balance) or your provider billing dashboard, then retry. Retrying without credits will keep failing.",
    );
  }
  const ollamaTimeout = parts.some((p) => /ollama\//i.test(p) && /timed out/i.test(p));
  const missingAuth = parts.filter((p) => /No API key found for provider/i.test(p));
  if (ollamaTimeout) {
    hints.push(
      "Ollama timed out — warm the model (`ollama run qwen3-coder:latest hi`), pick a smaller Ollama model in the composer, or raise agents.defaults.timeoutSeconds.",
    );
  }
  if (missingAuth.length > 0) {
    const providers = [
      ...new Set(
        missingAuth
          .map((p) => p.match(/provider "([^"]+)"/i)?.[1])
          .filter((name): name is string => Boolean(name)),
      ),
    ];
    hints.push(
      providers.length > 0
        ? `Missing API keys for: ${providers.join(", ")}. Run \`jaisiu models auth setup-token\` / \`jaisiu configure\`, or clear agents.defaults.model.fallbacks to use Ollama only.`
        : "Some fallbacks need API keys — configure auth or remove those fallbacks in ~/.openclaw/openclaw.json.",
    );
  }
  if (hints.length === 0) {
    return "Check `jaisiu models status`, fix the primary model, then retry. See gateway logs if Ollama or auth looks wrong.";
  }
  return hints.join(" ");
}

function formatRetryHint(retryAfterMs?: number): string | undefined {
  if (!retryAfterMs || retryAfterMs <= 0) {
    return "Wait a moment, then retry.";
  }
  const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
  return `Try again in about ${seconds}s.`;
}

const DETAIL_MAX_CHARS = 400;

function truncateDetail(text: string): string {
  if (text.length <= DETAIL_MAX_CHARS) {
    return text;
  }
  return `${text.slice(0, DETAIL_MAX_CHARS)}…(+${text.length - DETAIL_MAX_CHARS} chars)`;
}

function looksLikeJsonParseError(text: string): boolean {
  return /(Unterminated string in JSON|Unexpected token in JSON|Unexpected end of JSON input|JSON\.parse)/i.test(
    text,
  );
}

/** User-facing tool failure copy from buildEmbeddedRunPayloads (not a model outage). */
export function isSurfacedToolErrorMessage(message: string): boolean {
  const text = message.trim();
  if (!/^⚠️\s*\S+\s+\w+:/.test(text)) {
    return false;
  }
  if (!/\bfailed\b/i.test(text)) {
    return false;
  }
  if (
    /All models failed|Agent failed before reply|Model fallback exhausted|Context overflow|Conversation compaction failed/i.test(
      text,
    )
  ) {
    return false;
  }
  return true;
}

// JAISIU-observability-bore-pub-hang: word-boundary match so the literal
// "429" inside random UUIDs / lane names (e.g.
// `agent:main:fork:b7feb429-dd14-48bd-9264-1740e24ed79e`) does NOT
// trigger "Rate limit reached" for an unrelated error like
// `LaneActiveTimeoutError`. Mirrors the shape used by
// `src/agents/pi-embedded-subscribe.tools.ts:172`.
const RATE_LIMIT_TEXT_RE =
  /\b(429|529|http\s+429|http\s+529|rate[_ -]?limit|too many requests|throttl|quota exceeded|overloaded)\b/i;

const FORMATTED_STRUCTURED_TITLES = new Set([
  "All configured models failed",
  "Billing / credits exhausted",
  "Rate limit reached",
  "Context too large",
  "Request timed out",
  "Not authorized",
  "Session reset needs confirmation",
  "Something went wrong",
  "Tool command failed",
  "Disconnected from gateway",
  "Event stream gap",
  "Parallel run limit reached",
  "Delegation blocked",
  "Agent not allowed for delegation",
  "MiniMax authentication failed",
  "MiniMax tool-call ID mismatch",
  "Request failed",
  "Request unavailable",
  "Daily token budget reached",
  "Connection dropped mid-reply",
  // UX-PLAN Pillar G — guardrail blocks land here so `parseDisplayChatError`
  // round-trips cleanly through `parseFormattedStructuredChatError`.
  "Message blocked by guardrail",
]);

const BILLING_RECOVER =
  "Top up credits at https://pryzm.at (Account → Balance) or your model provider billing dashboard, then retry. Switching models will not help until credits are available.";

function looksLikeBillingMessage(text: string): boolean {
  return (
    /\b402\b/.test(text) ||
    /\(billing\)/i.test(text) ||
    /payment required|insufficient[_ ](?:credits|quota|balance)|credit balance|out of credits|run out of credits|monthly_token_quota|billing error/i.test(
      text,
    )
  );
}

/** Re-parse output from formatStructuredChatErrorPlain (title\\n\\ndetail\\n\\nrecover). */
export function parseFormattedStructuredChatError(raw: string): ChatErrorDisplay | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }
  const parts = text
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2 || !FORMATTED_STRUCTURED_TITLES.has(parts[0] ?? "")) {
    return null;
  }
  const title = parts[0] ?? "";
  const recover = parts.length >= 3 ? parts[parts.length - 1] : undefined;
  const detail = parts.length >= 3 ? parts.slice(1, -1).join("\n\n") : (parts[1] ?? "");
  return { title, detail, recover };
}

export function parseDisplayChatError(raw: string | null | undefined): ChatErrorDisplay | null {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) {
    return null;
  }
  return parseFormattedStructuredChatError(text) ?? parseChatError(text);
}

export function parseStructuredChatError(input: ChatErrorInput): ChatErrorDisplay {
  const text = input.message.trim();
  const code = input.code?.trim().toUpperCase();

  // UX-PLAN Pillar G: prefer the structured guardrail finding from
  // `input.details` when present; fall back to parsing the legacy
  // message string when details is missing.
  const guardrail =
    readGuardrailFindingFromDetails(input.details) ?? readGuardrailFindingFromMessage(text);

  if (guardrail) {
    return buildGuardrailErrorDisplay(guardrail);
  }

  if (code === "PAYMENT_REQUIRED" || looksLikeBillingMessage(text)) {
    return {
      title: "Billing / credits exhausted",
      detail: text,
      recover: BILLING_RECOVER,
    };
  }

  // JAISIU-observability-bore-pub-hang: the previous `/429|rate.?limit|rate_limit/i`
  // matched the literal "429" inside random UUIDs (e.g. a fork session
  // `agent:main:fork:b7feb429-...` rendered "Rate limit reached" for a
  // LaneActiveTimeoutError that had nothing to do with a 429). Require
  // word boundaries and gate on common rate-limit vocabulary so UUIDs
  // and lane names don't trigger this branch.
  if (code === "RATE_LIMITED" || RATE_LIMIT_TEXT_RE.test(text)) {
    return {
      title: "Rate limit reached",
      detail: text,
      recover:
        formatRetryHint(input.retryAfterMs) ??
        "Provider traffic is high. Wait briefly or switch model, then retry.",
    };
  }

  if (isSurfacedToolErrorMessage(text)) {
    return {
      title: "Tool command failed",
      detail: text.replace(/^⚠️\s*/, ""),
      recover:
        "The agent hit a tool or shell error (details are in the chat). Retry or adjust the request.",
    };
  }

  if (/All models failed/i.test(text)) {
    const modelRecover = buildAllModelsFailedRecover(text);
    const retryHint = formatRetryHint(input.retryAfterMs);
    return {
      title: looksLikeBillingMessage(text)
        ? "Billing / credits exhausted"
        : "All configured models failed",
      detail: text,
      recover: looksLikeBillingMessage(text)
        ? BILLING_RECOVER
        : retryHint
          ? `${modelRecover} ${retryHint}`
          : modelRecover,
    };
  }

  if (/daily token budget exceeded/i.test(text)) {
    return {
      title: "Daily token budget reached",
      detail: text,
      recover:
        "Developer boxes: set IRON_LOOP_DAILY_TOKEN_BUDGET=0 or JAISIU_DEV_MODE=1, then restart the gateway. Otherwise wait until UTC midnight or restart to reset the in-process counter.",
    };
  }

  if (code === "UNAVAILABLE") {
    const retryHint = formatRetryHint(input.retryAfterMs);
    if (/stream truncated|connection dropped before the reply finished/i.test(text)) {
      // The session-intact reassurance is the whole point of this branch, so
      // it always leads. `formatRetryHint` returns a non-empty string even
      // with no Retry-After, so the previous `retryHint ?? …` made the
      // reassurance dead code — it never rendered in the common case. Append
      // the server's specific delay only when it gave one.
      const intact =
        "Your session is intact — retry the same message. If it keeps happening, check gateway or broker connectivity.";
      return {
        title: "Connection dropped mid-reply",
        detail: text,
        recover:
          retryHint && /Try again in about/.test(retryHint) ? `${intact} ${retryHint}` : intact,
      };
    }
    return {
      title: "Request unavailable",
      detail: text,
      recover:
        retryHint ?? "Retry or start a new session. Check gateway connectivity if errors repeat.",
    };
  }

  if (
    /context window exceeds limit|exceeds model context window|context overflow|prompt too large/i.test(
      text,
    )
  ) {
    return {
      title: "Context too large",
      detail: text,
      recover:
        "Start a new session (/new), compact history, or switch to a larger-context model. The gateway will also retry with auto-compaction when possible.",
    };
  }

  if (code === "AGENT_TIMEOUT" || /gateway timeout|timed out/i.test(text)) {
    return {
      title: "Request timed out",
      detail: text,
      recover:
        formatRetryHint(input.retryAfterMs) ??
        "Retry with a shorter prompt or check gateway connectivity.",
    };
  }

  if (code === "UNAUTHORIZED") {
    return {
      title: "Not authorized",
      detail: truncateDetail(text),
      recover: "Check gateway auth token, device pairing, and session permissions.",
    };
  }

  if (looksLikeJsonParseError(text)) {
    return {
      title: "Gateway returned malformed data",
      detail: truncateDetail(text),
      recover:
        "The gateway or upstream model returned a truncated/invalid payload. Retry, or start a new session if it repeats. Check `jaisiu logs` for the failing frame.",
    };
  }

  return (
    parseChatError(text) ?? {
      title: "Something went wrong",
      detail: truncateDetail(text),
      recover: "Retry or start a new session. Check gateway connectivity if errors repeat.",
    }
  );
}

export function parseChatError(raw: string | null | undefined): ChatErrorDisplay | null {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) {
    return null;
  }

  // UX-PLAN Pillar G — fallback for legacy clients that don't pass
  // structured `details`. The message-string check is the same one
  // `parseStructuredChatError` does, but kept here so the no-input
  // path still surfaces a typed guardrail finding.
  const guardrail = readGuardrailFindingFromMessage(text);
  if (guardrail) {
    return buildGuardrailErrorDisplay(guardrail);
  }

  if (/sessions\.reset requires confirm:true|CONFIRMATION_REQUIRED/i.test(text)) {
    return {
      title: "Session reset needs confirmation",
      detail: text.replace(/^GatewayRequestError:\s*/i, ""),
      recover:
        "Use Reset on the session tab (you will be asked to confirm), or send /reset. This banner is not a model failure.",
    };
  }

  if (/^disconnected\s*\(/i.test(text) || /\bdisconnected\b/i.test(text)) {
    return {
      title: "Disconnected from gateway",
      detail: text,
      recover: "Reconnect using Overview or refresh the page. Unsaved draft stays in the composer.",
    };
  }

  if (/event gap detected/i.test(text)) {
    return {
      title: "Event stream gap",
      detail: text,
      recover: "Refresh the page or reconnect so chat history and runs stay in sync.",
    };
  }

  if (/subagent concurrency limit reached/i.test(text) || /concurrency limit/i.test(text)) {
    return {
      title: "Parallel run limit reached",
      detail: text,
      recover:
        "Wait for a subagent run to finish, stop one from Sessions, or raise agents.defaults.subagents.maxConcurrent in config.",
    };
  }

  if (/sessions_spawn is not allowed from sub-agent/i.test(text)) {
    return {
      title: "Delegation blocked",
      detail: text,
      recover: "Spawn subagents from the main session only.",
    };
  }

  if (/agentId is not allowed for sessions_spawn/i.test(text)) {
    return {
      title: "Agent not allowed for delegation",
      detail: text,
      recover: "Adjust agents.<id>.subagents.allowAgents in config or use an allowed agent id.",
    };
  }

  if (/invalid api key/i.test(text) && /\b401\b/.test(text)) {
    return {
      title: "MiniMax authentication failed",
      detail: text,
      recover:
        "Use model minimax-portal/MiniMax-M2.7 (not minimax/…) for sk-cp-* keys, then restart the gateway. Refresh this page after a successful reply.",
    };
  }

  if (/tool result'?s tool id/i.test(text) && /not found/i.test(text)) {
    return {
      title: "MiniMax tool-call ID mismatch",
      detail: text,
      recover:
        "Session tool history is out of sync. Use /new, keep minimax-portal/MiniMax-M2.7 as primary, and remove minimax/MiniMax-M2.1 from fallbacks for tool-heavy sessions.",
    };
  }

  if (/build incomplete:/i.test(text) || /never wrote files or ran commands/i.test(text)) {
    return {
      title: "Build incomplete",
      detail: text,
      recover:
        "Retry with a single-file prompt and explicit write/exec steps. For local Qwen, start a fresh session if thinking context is bloated, or switch to MiniMax for tool-heavy builds.",
    };
  }

  if (/stopped before giving you a clear summary/i.test(text)) {
    return {
      title: "Summary incomplete",
      detail: text,
      recover: "Reply continue to get a synthesized wrap-up, or retry the request.",
    };
  }

  if (isSurfacedToolErrorMessage(text)) {
    return {
      title: "Tool command failed",
      detail: text.replace(/^⚠️\s*/, ""),
      recover:
        "The agent hit a tool or shell error (details are in the chat). Retry or adjust the request.",
    };
  }

  if (/^Error:\s*/i.test(text) || text.startsWith("RPC ") || text.includes("JSON-RPC")) {
    return {
      title: "Request failed",
      detail: text.replace(/^Error:\s*/i, ""),
      recover: "Retry the action. If it persists, check gateway logs and connection.",
    };
  }

  return {
    title: "Something went wrong",
    detail: text,
    recover: "Retry or start a new session. Check gateway connectivity if errors repeat.",
  };
}

// ---------- Guardrail display builder ----------

const GUARDRAIL_RULE_LABELS: Record<string, string> = {
  max_length: "Message too long",
  min_length: "Message too short",
  attachment_max_length: "Attachment too large",
  prompt_injection: "Possible prompt injection",
  pii: "PII detected",
};

const GUARDRAIL_RULE_RECOVERS: Record<string, string> = {
  max_length:
    "Shorten the message or split it into smaller turns. The Truncate-and-retry button drafts a shorter version you can edit before sending.",
  min_length: "Add a sentence or attach a file so the message isn't blank.",
  attachment_max_length:
    "Trim the attachment or send it across multiple turns. The Truncate-and-retry button keeps the text and drops the oversized attachment.",
  prompt_injection:
    "Rephrase the message so it does not match the server's injection pattern. The gateway blocks attempts to override system instructions.",
  pii: "Remove the personal data (SSN / CC / phone / email / IP) and retry, or disable the PII rejection rule in cfg.session.guardrails.input.rejectPii.",
};

function buildGuardrailErrorDisplay(finding: ChatGuardrailFinding): ChatErrorDisplay {
  const label = GUARDRAIL_RULE_LABELS[finding.rule] ?? "Blocked by chat guardrail";
  const recover =
    GUARDRAIL_RULE_RECOVERS[finding.rule] ??
    "The gateway's chat guardrail blocked this send. Adjust the input or relax the rule in cfg.session.guardrails, then retry.";
  const detail =
    finding.message && finding.message.length > 0 ? finding.message : `Rule: ${finding.rule}`;
  return {
    title: "Message blocked by guardrail",
    detail,
    recover,
    guardrail: finding,
  };
}

export function formatChatErrorPlain(raw: string | null | undefined): string | null {
  const parsed = parseChatError(raw);
  if (!parsed) {
    return null;
  }
  return [parsed.title, parsed.detail, parsed.recover].filter(Boolean).join("\n\n");
}

export function formatStructuredChatErrorPlain(input: ChatErrorInput): string {
  const parsed = parseStructuredChatError(input);
  return [parsed.title, parsed.detail, parsed.recover].filter(Boolean).join("\n\n");
}

export function chatErrorInputFromGatewayShape(shape: GatewayErrorShape): ChatErrorInput {
  return {
    message: shape.message,
    code: shape.code,
    retryable: shape.retryable,
    retryAfterMs: shape.retryAfterMs,
    // UX-PLAN Pillar G — propagate the structured details so guardrail
    // findings on `ErrorShape.details.guardrail` reach the chat-errors
    // pipeline as typed `ChatGuardrailFinding` instead of staying trapped
    // inside the wire-level error shape.
    details: shape.details,
  };
}

export function chatErrorInputFromEvent(payload: {
  errorMessage?: string;
  errorCode?: string;
  retryable?: boolean;
  retryAfterMs?: number;
  /** UX-PLAN Pillar G — passed through to `ChatErrorInput.details`. */
  details?: unknown;
}): ChatErrorInput {
  return {
    message: payload.errorMessage ?? "chat error",
    code: payload.errorCode,
    retryable: payload.retryable,
    retryAfterMs: payload.retryAfterMs,
    details: payload.details,
  };
}
